import { useState, type FormEvent } from "react";
import { RECORD_TYPES, normalizeRecordType, type RecordType } from "../lib/recordTypes";

/**
 * Subscribe form for DNS change alerts.
 *
 * The success copy deliberately says "check your email" rather than "you're
 * subscribed", because at this point nothing is subscribed — the address has
 * to confirm first. Overstating it here is how people end up believing they're
 * being monitored when they aren't.
 */
export function WatchForm({ initialHost = "", initialType = "A" }: { initialHost?: string; initialType?: string }) {
  const [email, setEmail] = useState("");
  const [host, setHost] = useState(initialHost);
  // Normalize, don't trust: the console links here with any of the 14 types,
  // and a value with no matching <option> renders as "A" while state keeps
  // the real value — the form would then subscribe to a type it never showed.
  const [type, setType] = useState<RecordType>(normalizeRecordType(initialType));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch("/api/watch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, host: normalizeHost(host), type }),
      });
      const body = (await res.json()) as { ok?: boolean; message?: string };
      setResult({ ok: !!body.ok, message: String(body.message ?? "Something went wrong.") });
      if (body.ok) setEmail("");
    } catch {
      setResult({ ok: false, message: "Couldn't reach the server. Try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="watch-panel">
      <div className="panel-title">GET AN EMAIL WHEN THIS CHANGES</div>
      <form onSubmit={submit} className="watch-form">
        <div className="watch-row">
          <label className="watch-field">
            <span className="gen-label">Hostname</span>
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="example.com"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              required
            />
          </label>
          <label className="watch-field watch-type">
            <span className="gen-label">Record</span>
            <select value={type} onChange={(e) => setType(normalizeRecordType(e.target.value))} aria-label="Record type">
              {RECORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <label className="watch-field">
          <span className="gen-label">Your email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            required
          />
        </label>
        <button type="submit" className="watch-submit" disabled={busy || !email.trim() || !host.trim()}>
          {busy ? "Sending…" : "Send me a confirmation link"}
        </button>
        <p className="watch-fineprint">
          We email you a link first — the watch only starts once you click it. One click to
          unsubscribe, no account needed. Your address is used for these alerts and nothing else.
          See the <a href="/privacy">privacy policy</a>.
        </p>
      </form>

      {result ? (
        <p className={`watch-result ${result.ok ? "watch-ok" : "watch-err"}`} role="status" aria-live="polite">
          {result.message}
        </p>
      ) : null}
    </section>
  );
}

function normalizeHost(raw: string): string {
  return raw.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}
